import { FIPAV_SOURCE_ORIGIN_HOSTS, fipavSource } from '@/lib/fipav/source'

/**
 * URL del proxy interno `/api/logo` per un logo (FIPAV o locale).
 *
 * I loghi FIPAV vanno **sempre** serviti passando dal microservizio webdata
 * (`WP_WEBDATA_URL` → `/proxy`) e mai puntati direttamente al sito FIPAV: il
 * sito risponde 404 alle richieste senza User-Agent/Referer da browser e non
 * offre cache. `/api/logo` incapsula tutto questo (proxy + rimozione sfondo +
 * cache) ed è la convenzione usata da portale, classifiche e area admin.
 *
 * Funzione pura, utilizzabile sia da Server sia da Client Component.
 */

/**
 * Host esterni ammessi dal proxy `/api/logo`.
 *
 * È la **stessa** lista usata dalla route per validare `src`: tenerla qui evita
 * che i due elenchi divergano (un host assente qui → 403 dal proxy).
 *
 * PERCHÉ c'è più di un host FIPAV: le origini dei comitati supportati (Puglia,
 * Veneto, Emilia-Romagna) più quella **risolta** dell'istanza. Quest'ultima è
 * necessaria perché questo modulo è importato anche da **client component**
 * (scorebug, hero club, …), dove le env non-`NEXT_PUBLIC_` sono `undefined`: senza
 * l'elenco completo un'istanza Veneto/ER costruirebbe lato client un'allowlist
 * ridotta al solo host pugliese e smetterebbe di proxare i propri loghi.
 */
export const LOGO_PROXY_ALLOWED_HOSTS: Set<string> = new Set<string>([
  ...FIPAV_SOURCE_ORIGIN_HOSTS,
  new URL(fipavSource().origin).hostname,
  'www.fipav.it',
  'legavolley.it',
  'www.legavolley.it',
])

/** True se `src` è un URL FIPAV/Lega Volley servibile dal proxy immagini. */
function isFipavLogoUrl(src: string | null | undefined): boolean {
  if (!src || src.startsWith('/')) return false
  try {
    const url = new URL(src)
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      LOGO_PROXY_ALLOWED_HOSTS.has(url.hostname)
    )
  } catch {
    return false
  }
}

/**
 * `width` chiede al proxy di **ridurre l'immagine lato server** (Lanczos) alla
 * larghezza indicata. Senza, il browser rimpicciolisce un'immagine da 1500+ px a
 * poche decine di px in un passo solo e i tratti sottili spariscono.
 */
export function logoProxyUrl(src: string | null | undefined, width?: number): string | null {
  if (!src) return null
  const w = width && width >= 32 ? `&w=${Math.min(2048, Math.round(width))}` : ''
  return `/api/logo?src=${encodeURIComponent(src)}${w}`
}

/**
 * Proxa via `/api/logo` **solo** i loghi FIPAV/Lega Volley; per tutti gli altri
 * (upload locali, URL esterni arbitrari) restituisce l'URL originale.
 *
 * Usata sulle superfici dove un operatore può inserire un logo qualsiasi
 * (es. live/scorebug): proxare un host non in allowlist darebbe 403 e logo rotto.
 */
export function fipavLogoProxyUrl(src: string | null | undefined, width?: number): string | null {
  if (!src) return null
  return isFipavLogoUrl(src) ? logoProxyUrl(src, width) : src
}
